import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ID, slug } from '../../lib/ids';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { ErrorNotice } from '@bounded/ui/state';
import { Button, Empty, Field, Input, Page, PageHeader, Panel, Skeleton, Table, col } from '@bounded/ui/primitives';
import { useT } from '../../i18n';

export const Route = createFileRoute('/_app/projects/')({ component: Projects });


function Projects() {
  const t = useT(); const m = t.projects;
  const overview = useQuery(q.overview());
  const [creating, setCreating] = useState(false);
  const projects = overview.data?.projects;
  return (
    <Page>
      <PageHeader title={m.title} description={m.description} actions={!creating && <Button variant="primary" onClick={() => setCreating(true)}>{m.new}</Button>} />
      {creating && <NewProject onCancel={() => setCreating(false)} />}
      <Panel>
        {overview.isPending && <div className="p-4"><Skeleton className="h-24" /></div>}
        {overview.error && <div className="p-4"><ErrorNotice error={overview.error} onRetry={() => void overview.refetch()} /></div>}
        {projects?.length === 0 && !creating && <Empty title={m.emptyTitle} action={<Button onClick={() => setCreating(true)}>{m.createFirst}</Button>}>{m.emptyBody}</Empty>}
        {!!projects?.length && (
          <Table head={[{ label: m.cols.project }, { label: m.cols.inEffect }, { label: m.cols.id, className: col.hideSm }]} minWidth="0">
            {projects.map((p) => (
              <tr key={p.id} className="hover:bg-sunken">
                <td><Link to="/projects/$projectId" params={{ projectId: p.id }} className="font-medium hover:underline">{p.name}</Link></td>
                <td className="text-graphite">{p.targets ? m.inEffect(p.applied, p.targets, p.rejected) : m.noDevices}</td>
                <td className={col.hideSm}><code className="text-graphite">{p.id}</code></td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </Page>
  );
}

function NewProject({ onCancel }: { onCancel: () => void }) {
  const t = useT(); const m = t.projects;
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [idEdited, setIdEdited] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectId = idEdited ? id : slug(name);
  const create = useMutation({
    mutationFn: () => api.saveProject(projectId, name.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['overview'] });
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await navigate({ to: '/projects/$projectId/policy', params: { projectId } });
    },
  });
  const validId = ID.test(projectId);
  return (
    <Panel title={m.newTitle}>
      <form className="grid max-w-3xl gap-4 p-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        {create.error && <div className="sm:col-span-2"><ErrorNotice error={create.error} /></div>}
        <Field label={m.name} htmlFor="project-name"><Input id="project-name" autoFocus required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="bounded" /></Field>
        <Field label={m.id} htmlFor="project-id" hint={projectId && !validId ? m.idInvalid : m.idHint}>
          <Input id="project-id" required className="font-mono" value={projectId} onChange={(e) => { setIdEdited(true); setId(e.target.value); }} />
        </Field>
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={!name.trim() || !validId || create.isPending}>{m.create}</Button>
          <Button variant="ghost" onClick={onCancel}>{t.common.cancel}</Button>
        </div>
      </form>
    </Panel>
  );
}
